import {CatalogTabMenuItems, useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {React, classNames, action, computed, observable, observer, toJS, trace, useAsObservableSource, useLocalStore, utils, autorun} from '@api'
import {PrimaryIconButton} from 'centric-ui-components'
import {IDisposer} from 'mobx-utils'
import * as css from './TabButton.css'
import {Fragment, Ref, useEffect, useLayoutEffect} from 'react'
import {CatalogRootFlexLayoutStoreContext, VipFlexLayoutTabStore, MyFlexTabCloseButton, useCatalogRootFlexLayoutStore, VipFlexLayoutTabStoreContext} from '@stores/catalog/flexLayout'
import {globalStores} from '@stores'
import {Popover2Props} from '@api'
import {Actions, BorderNode, CLASSES, I18nLabel, ICloseType, ILayoutCallbacks, Rect, TabNode, TabSetNode} from '@flexlayout'
import {IIcons, ITitleObject, Layout, LayoutStore} from './Layout'

const defaultPopoverMenuProps: Partial<Popover2Props> = {
  interactionKind: 'hover',
  portalClassName: css.menuPortal,
  hoverOpenDelay: 500,
  hoverCloseDelay: 300,
  lazy: true,
  usePortal: true,
  minimal: true,
  modifiers: {
    offset: {
      enabled: true,
      options: {
        offset: [-10, 1]
      }
    }
  },
  position: 'bottom-left'
}

/** @hidden @internal */
export interface ITabButtonProps {
  layout: ILayoutCallbacks;
  node: TabNode;
  show?: boolean;
  selected: boolean;
  height?: number;
  iconFactory?: (node: TabNode) => React.ReactNode | undefined;
  titleFactory?: (node: TabNode) => React.ReactNode | undefined;
  icons?: IIcons;
}

export class TabButtonStore extends VipLocalStore implements Partial<ITabButtonProps> {
  constructor(s, public props: ITabButtonProps) {
    super(s)
  }

  get layout() { return (this.props.layout as LayoutStore)}

  get node() { return this.props.node}

  get selected() { return this.props.selected}

  get show() { return this.props.show}

  get height() { return this.props.height}

  get flexLayout() { return globalStores.flexLayout}

  _toDispose: Array<IDisposer> = []

  dispose = () => {
    this._toDispose.forEach(f => f())
  }

  get scrollContainer() {
    return this.ref.closest('.flexlayout__tabset_tabbar_inner')
  }

  registerEffects = () => {
    const {_toDispose} = this

    _toDispose.push(
      autorun(() => {
        this.props.node.tabStore = this
      })
    )
  }

  @computed get isMenuOpen() {
    return !this.node.isPreviewLayout && this.flexLayout.tabMenuNode == this.node.id
  }

  @action onMouseDown = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if ((event as React.MouseEvent<HTMLDivElement>).button === 2) return // Todo - what about the touch event with 2 fingers?

    if (!event.currentTarget.contains(event.target as HTMLElement)) {
      return
    }

    const {layout: {editingTab, dragStart, i18nName}, node, onClick, onDoubleClick} = this

    if (!editingTab) {
      const message = i18nName(I18nLabel.Move_Tab, node.displayName)
      dragStart(event, message, node, node.isEnableDrag, onClick, onDoubleClick)
    }
  }

  @action onClick = (e) => {
    const {layout, flexLayout, node} = this

    if (node.isGroupLabel) {
      node.attributes.groupExpanded = !node.attributes.groupExpanded
      node.model._changeListener()
    } else {
      layout.doAction(Actions.selectTab(node.id))
    }
  }

  @action onBeginEdit = (event: Event) => {
    const {layout: {setEditingTab}, node} = this
    setEditingTab(node)
  }

  @action onDoubleClick = (event: Event) => {
    const {flexLayout, node, onBeginEdit} = this

    if (node.isGroupLabel) {
      node.attributes.groupExpanded = !node.attributes.groupExpanded
      node.model._changeListener()
    } else {
      // Confusing and not reliable
      flexLayout.onDoubleClick_forTabNode(node)(event as any)

      // if (node.isEnableRename) {
      //   onBeginEdit(event)
      // }
      // else {
      // }
      // else {
      //     const parentNode = node.parent as TabSetNode;
      //     if (parentNode.canMaximize) {
      //         layout.maximize(parentNode);
      //     }
      // }
    }
  }

  @observable.ref innerContainer: HTMLDivElement
  @observable.ref contentRef: HTMLDivElement | HTMLInputElement

  @action onTextBoxFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select()
  }

  @computed get isClosable() {
    const {node, selected} = this
    const closeType = node.closeType
    if (selected || closeType === ICloseType.Always) {
      return true
    }
    if (closeType === ICloseType.Visible) {
      // not selected but x should be visible due to hover
      if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        return true
      }
    }
    return false
  }

  @action onClose = (event: React.MouseEvent<HTMLDivElement>) => {
    const {isClosable, layout, node, onClick} = this
    if (isClosable) {
      layout.doAction(Actions.deleteTab(node.id))
    } else {
      onClick(event)
    }
  }
  @action onCloseMouseDown = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    event.stopPropagation()
  }

  @computed get collapsedViaGroup() {
    const {node: {associatedGroup, isGroupLabel, groupExpanded}} = this
    return associatedGroup && !isGroupLabel && !groupExpanded
  }

  @computed get style(): React.CSSProperties {
    const {props: {show}, node: {associatedGroup}, collapsedViaGroup} = this
    // console.log(toJS({id, associatedGroup}))

    return {
      visibility: show && !collapsedViaGroup ? 'visible' : 'hidden',
      '--group-background-color': associatedGroup ? associatedGroup?.groupColor : undefined
    } as React.CSSProperties
  }

  @action updateRect = () => {
    const {node, layout, innerContainer, contentRef} = this
    try {
      // record position of tab in node
      const layoutRect = layout.domRect
      const r = innerContainer?.getBoundingClientRect() ?? {left: 0, top: 0, width: 0, height: 0}
      node._setTabRect(new Rect(r.left - layoutRect.x, r.top - layoutRect.y, r.width, r.height))
    } catch (err) {
      console.error(err)
    }
  }

  @action onTextBoxMouseDown = (event: React.MouseEvent<HTMLInputElement> | React.TouchEvent<HTMLInputElement>) => {
    // console.log("onTextBoxMouseDown");
    event.stopPropagation()
  }

  @action commitRename = () => {
    const {layout, node, contentRef} = this
    layout.setEditingTab(undefined)
    if (contentRef instanceof HTMLInputElement) {
      layout.doAction(Actions.renameTab(node.id, contentRef.value))
    }
  }

  @action onTextBoxKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const {layout, node} = this
    // console.log(event, event.keyCode);
    if (e.keyCode === 27) {
      // esc
      layout.setEditingTab(undefined)
    } else if (e.keyCode === 13) {
      // enter
     this.commitRename()
    }
  }

  isTitleObject(obj: any): obj is ITitleObject {
    return obj.titleContent !== undefined
  }

  @computed get parentNode() { return this.node.parent as TabSetNode}

  @computed get titleContent() {
    const {node, props: {titleFactory}, isTitleObject} = this
    let titleContent: React.ReactNode = node.name
    let name = node.name

    if (titleFactory !== undefined) {
      const titleObj = titleFactory(node)
      if (titleObj !== undefined) {
        if (typeof titleObj === 'string') {
          titleContent = titleObj as string
          name = titleObj as string
        } else if (isTitleObject(titleObj)) {
          titleContent = titleObj.titleContent
          name = titleObj.name
        } else {
          titleContent = titleObj
        }
      }
    }

    return {titleContent, name}
  }

  @computed get leadingContent() {
    const {props: {iconFactory}, node} = this
    let leadingContent = iconFactory ? iconFactory(node) : undefined

    if (typeof leadingContent === undefined && typeof node.icon !== undefined) {
      leadingContent = <img src={node.icon} alt='leadingContent'/>
    }

    return leadingContent
  }

  @computed get className() {
    const {layout: {getClassName: cm}, node: {isInBorder}} = this

    if (isInBorder) {
      const {node: {border: {location: {name: border}}, selectedInBorder, className}} = this
      return classNames(
        cm(CLASSES.FLEXLAYOUT__BORDER_BUTTON),
        cm(CLASSES.FLEXLAYOUT__BORDER_BUTTON_ + border),
        selectedInBorder ? cm(CLASSES.FLEXLAYOUT__BORDER_BUTTON__SELECTED) : cm(CLASSES.FLEXLAYOUT__BORDER_BUTTON__UNSELECTED),
        className
      )
    }
    else {

    }

    return null
  }

  @computed get renderState() {
    const {leadingContent, props: {icons}, layout: {getClassName: cm}, onCloseMouseDown, onClose, titleContent: {titleContent, name}, layout, node} = this
    let buttons: any[] = []
    // allow customization of leading contents (icon) and contents
    const renderState = {leading: leadingContent, content: titleContent, name, buttons}
    layout.customizeTab(node, renderState)

    if (node.isEnableClose) {
      if (true) {
        buttons.push(
          <MyFlexTabCloseButton key='close' className={cm(CLASSES.FLEXLAYOUT__TAB_BUTTON_TRAILING)} onMouseDown={onCloseMouseDown} onClick={onClose} onTouchStart={onCloseMouseDown}/>
        )
      } else {
        const closeTitle = layout.i18nName(I18nLabel.Close_Tab)
        buttons.push(
          <div key='close' title={closeTitle} className={cm(CLASSES.FLEXLAYOUT__TAB_BUTTON_TRAILING)} onMouseDown={onCloseMouseDown} onClick={onClose} onTouchStart={onCloseMouseDown}>
            {icons?.close}
          </div>
        )
      }
    }

    return renderState
  }

  get cm() { return this.layout.getClassName}

  @computed get leading() {
    return <div className={classNames(css.leading, this.cm(CLASSES.FLEXLAYOUT__TAB_BUTTON_LEADING))}>{this.renderState.leading}</div>
  }

  get ref() { return this.node.ref}

  setRef = (ref: HTMLDivElement) => this.node.ref = ref
}

export const TabButtonStoreContext = React.createContext<TabButtonStore>(null)
export const useTabButtonStore = () => React.useContext(TabButtonStoreContext)

/** @hidden @internal */
export const TabButton = observer<ITabButtonProps>(function TabButton(props: ITabButtonProps) {
  const {node} = props,
    {tabHeader_onMouseEnter, tabHeader_onMouseLeave} = node,
    selfRef = React.useRef<HTMLDivElement | null>(null),
    {flexLayout} = useStores(),
    obsProps = useAsObservableSource(props),
    store = useVipLocalStore((s) => new TabButtonStore(s, obsProps))

  useLayoutEffect(() => store.registerEffects(), [])

  useLayoutEffect(() => {
    store.innerContainer = selfRef.current
  }, [selfRef.current])

  const {onMouseDown, leading, node: {displayName}, renderState} = store

  useLayoutEffect(() => {
    store.updateRect()
  }, [node.isSelected, node.isVisible, node.tabSet.selected, selfRef.current, store.layout.domRect])

  const tabMenuStoreProps = useAsObservableSource({flexNode: node}),
    tabStore = useVipLocalStore(s => new VipFlexLayoutTabStore(s, tabMenuStoreProps)),
    {buttons} = renderState,
    {isGroupLabel, associatedGroup, groupExpanded, indexInTabGroup, selectedIndexInGroup} = node,
    {setRef} = store

  const contents = <Fragment>
    {leading}
    <TabButtonContent/>
    {buttons}
  </Fragment>

  // trace()
  // console.log({id: node.id, className: node.tabButtonClassName, selectedIndexInGroup, name: node.name, isMenuOpen: store.isMenuOpen })

  return (
    <TabButtonStoreContext.Provider value={store}>
      <VipFlexLayoutTabStoreContext.Provider value={tabStore}>
        <CatalogRootFlexLayoutStoreContext.Provider value={node.catalogRoot}>
          <div
            ref={setRef}
            style={store.style}
            tabIndex={node.indexInParent}
            data-id={node.id}
            data-component={node.component}
            data-focused={node.id === flexLayout.activeTab?.id}
            data-group-label={isGroupLabel === true ? true : undefined}
            data-group={associatedGroup?.id}
            data-group-index={indexInTabGroup}
            data-type='TabButton'
            className={classNames(css.root, node.tabButtonClassName)}
            data-expanded={associatedGroup ? groupExpanded === true : undefined}
            data-selected-index={isGroupLabel ? selectedIndexInGroup : undefined}
            onContextMenu={flexLayout.onContextMenu_forTabNode(node)}
            onMouseEnter={tabHeader_onMouseEnter}
            onMouseLeave={tabHeader_onMouseLeave}
          >
            {/*<Popover2*/}
            {/*  {...defaultPopoverMenuProps}*/}
            {/*  isOpen={store.isMenuOpen}*/}
            {/*  onInteraction={flexLayout.tabMenuInteraction(node.id)}*/}
            {/*  disabled={node.isPreviewLayout || !node.tabSet || flexLayout.knownTabs[node.id] != null || layout.editingTab === node} // || node.isGroupLabel*/}
            {/*  onOpening={() => flexLayout.customTabMenuOpening(node.id)}*/}
            {/*  onClosing={flexLayout.customTabMenuClosing}*/}
            {/*  className={css.popoverWrapper}*/}
            {/*  content={<Menu className={css.tabMenu}>*/}
            {/*    <CatalogTabMenuItems/>*/}
            {/*  </Menu>}*/}
            {/*>*/}
            <div className={css.innerContainer} ref={selfRef}
                 onDoubleClick={flexLayout.onDoubleClick_forTabNode(node)}
                 onMouseDown={onMouseDown}
                 onTouchStart={onMouseDown}
            >
              {isGroupLabel ? <div className={classNames(css.groupLabelBorderContainer, 'groupLabelBorderContainer')}>{contents}</div> : contents}
            </div>
            {/*</Popover2>*/}
          </div>

          <AddItemToEndOfTabGroup/>
        </CatalogRootFlexLayoutStoreContext.Provider>
      </VipFlexLayoutTabStoreContext.Provider>
    </TabButtonStoreContext.Provider>
  )
})

export const AddItemToEndOfTabGroup = observer(function AddItemToEndOfTabGroup() {
  const {node: {isLastItemInTabGroup, groupExpanded, associatedGroup}, flexLayout} = useTabButtonStore()
  return isLastItemInTabGroup && groupExpanded ? (
    <PrimaryIconButton
      icon='plus'
      wrapperClassName={classNames(css.newTabButton)}
      tooltipPosition='bottom'
      tooltip='New Item in Group'
      onMouseDown={utils.eatMouseEvent}
      onClick={(e) => {
        utils.eatMouseEvent(e)
        flexLayout.newTabInGroup(associatedGroup, true)
      }}
    />
  ) : null
})

const TabButtonContent = observer(function TabButtonContent() {
  const store = useTabButtonStore(),
    {cm, stores: {flexLayoutCatalogRoot: catalogRoot}, flexLayout, renderState, layout, node, commitRename, onTextBoxFocus, onTextBoxMouseDown, onTextBoxKeyPress} = store,
    contentRef = React.useRef<HTMLInputElement | null>(null)

  useLayoutEffect(() => {
    node.contentRef = contentRef.current!
    store.contentRef = contentRef.current
  }, [contentRef.current])

  useLayoutEffect(() => {
    if (node.isEditing) {
      (contentRef.current as HTMLInputElement)?.select()
    }
  })

  if (layout.editingTab === node) {
    const width = contentRef.current.getBoundingClientRect().width
    return (
      <input
        key='rename'
        style={{minWidth: `${width || 60}px`} as any}
        ref={contentRef}
        className={classNames(css.renameInput, cm(CLASSES.FLEXLAYOUT__TAB_BUTTON_TEXTBOX))}
        type='text'
        autoFocus={true}
        onFocus={onTextBoxFocus}
        defaultValue={node.displayName}
        placeholder={catalogRoot ? flexLayout.defaultCatalogNameFor(node) : undefined}
        onKeyDown={onTextBoxKeyPress}
        onMouseDown={onTextBoxMouseDown}
        onTouchStart={onTextBoxMouseDown}
        onBlur={commitRename}
      />
    )
  }

  return (
    <div ref={contentRef} className={cm(CLASSES.FLEXLAYOUT__TAB_BUTTON_CONTENT)}>
      {renderState.content}
    </div>
  )
})



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/view/TabButton.tsx